import { useEffect, useState } from "react";
import { Code2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LeadExtraFields, SOURCES } from "@/components/LeadFields";
import { api, errMsg, label } from "@/lib/api";

const STATUSES = ["NEW_INQUIRY", "CONTACTED", "REQUIREMENT_GATHERED", "QUOTATION_SENT", "NEGOTIATION", "WON_APPROVED", "LOST"];
const FIELDS = ["clientName", "clientPhone", "clientEmail", "clientCity", "budget", "source", "status", "requirements", "notes", "useCase", "contactChannel", "deliveryTimeline", "companyName", "position"];

export const iframeCode = () => `<iframe src="${window.location.origin}/lead-form" width="100%" height="700" frameborder="0"></iframe>`;

export function CopyIframeButton({ variant = "outline" }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(iframeCode()); toast.success("iFrame code copied"); }
    catch { toast.message(iframeCode()); }
  };
  return <Button variant={variant} onClick={copy} data-testid="copy-iframe-button"><Code2 className="h-4 w-4 mr-2" />Copy iFrame Code</Button>;
}

export function LeadEditDialog({ lead, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => { if (lead) setF(Object.fromEntries(FIELDS.map((k) => [k, lead[k] ?? ""]))); }, [lead]);
  if (!lead || !f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try { await api.patch(`/leads/${lead.id}`, { ...f, budget: +f.budget || 0 }); toast.success("Lead updated"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sel = (k, opts, fmt = (x) => x) => (
    <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
      <SelectTrigger data-testid={`edit-lead-${k}-select`}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{fmt(o)}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="edit-lead-dialog">
        <DialogHeader><DialogTitle>Edit {lead.leadNumber}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5"><Label>Client name</Label><Input value={f.clientName} onChange={set("clientName")} data-testid="edit-lead-name-input" /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={f.clientPhone} onChange={set("clientPhone")} data-testid="edit-lead-phone-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={f.clientEmail} onChange={set("clientEmail")} data-testid="edit-lead-email-input" /></div>
          <div className="space-y-1.5"><Label>City</Label><Input value={f.clientCity} onChange={set("clientCity")} data-testid="edit-lead-city-input" /></div>
          <div className="space-y-1.5"><Label>Budget (₹)</Label><Input type="number" value={f.budget} onChange={set("budget")} data-testid="edit-lead-budget-input" /></div>
          <div className="space-y-1.5"><Label>Source</Label>{sel("source", SOURCES)}</div>
          <div className="space-y-1.5"><Label>Status</Label>{sel("status", STATUSES, label)}</div>
          <LeadExtraFields f={f} setF={setF} prefix="edit-lead" />
          <div className="col-span-2 space-y-1.5"><Label>Notes / requests</Label><Textarea rows={2} value={f.requirements} onChange={set("requirements")} data-testid="edit-lead-requirements-input" /></div>
          <div className="col-span-2 space-y-1.5"><Label>Internal notes</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="edit-lead-notes-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="edit-lead-save">Save changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
